import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Crown, CheckCircle, Zap, Star, Eye, Music } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MembershipPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    // Load plans from API (admin-editable in DB)
    axios.get(`${API_URL}/api/memberships/`).then(r => {
      const apiTiers = r.data.map(tier => ({
        id: tier.id,
        name: tier.name,
        price: tier.price,
        priceRange: null,
        downloads_per_month: tier.downloads_per_month,
        features: tier.features,
        is_ultimate: tier.is_ultimate,
        custom_songs_count: tier.custom_songs_count,
        bestEntry: tier.id === 'og-access',
        popular: tier.id === 'og-pro',
        exclusive: tier.id === 'og-unlimited',
      }));
      setTiers(apiTiers);
    }).catch(() => {
      // Fallback
      setTiers([
        { id: 'og-access', name: 'OG Access', price: 14.99, downloads_per_month: 100, features: [], bestEntry: true },
        { id: 'og-pro', name: 'OG Pro', price: 35.99, downloads_per_month: 200, features: [], popular: true },
        { id: 'og-unlimited', name: 'OG Unlimited', price: 99, downloads_per_month: -1, features: [], exclusive: true },
      ]);
    });
  }, []);

  const handleJoin = (tierId) => {
    navigate(`/checkout?type=membership&id=${tierId}`);
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="section-padding luxury-mesh">
        <div className="container-custom text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">
            {t('membership.overline')}
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {t('membership.title')} <span className="gold-text">{t('membership.titleHighlight')}</span>
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-8">
            {t('membership.subtitle')}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tiers.map((tier) => (
              <div 
                key={tier.id}
                className={`glass-card p-6 relative ${
                  tier.is_ultimate ? 'bg-gradient-to-br from-[#E8720C]/15 via-purple-500/5 to-transparent border-[#E8720C]/40 ring-2 ring-[#E8720C]/30 shadow-[0_0_60px_-15px_rgba(232,114,12,0.4)]' : ''
                } ${tier.exclusive && !tier.is_ultimate ? 'bg-black border-[#E8720C]/30 ring-1 ring-[#E8720C]/20' : ''
                } ${tier.popular ? 'ring-1 ring-white/20' : ''} ${tier.bestEntry ? 'ring-2 ring-[#E8720C]/40 bg-[#E8720C]/5' : ''}`}
                data-testid={`membership-card-${tier.id}`}
              >
                {tier.is_ultimate && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-purple-500 text-white rounded-full flex items-center gap-1 shadow-lg">
                      <Crown className="w-3 h-3" /> Ultimate Pack
                    </span>
                  </div>
                )}
                {tier.bestEntry && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-[#E8720C] text-white rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Best Start
                    </span>
                  </div>
                )}
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-white text-black rounded-full flex items-center gap-1">
                      <Zap className="w-3 h-3" /> {t('membership.bestValue')}
                    </span>
                  </div>
                )}
                {tier.exclusive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-black rounded-full flex items-center gap-1">
                      <Crown className="w-3 h-3" /> {t('membership.premium')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8 pt-4">
                  <h3 className={`text-2xl font-semibold mb-2 ${tier.exclusive ? 'gold-text' : ''}`}>
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-5xl font-bold ${tier.exclusive ? 'gold-text' : ''}`}>
                      ${tier.price % 1 === 0 ? tier.price : tier.price.toFixed(2)}
                    </span>
                    <span className="text-white/40">{t('membership.perMonth')}</span>
                  </div>
                  <p className="text-sm text-white/50 mt-3">
                    {tier.downloads_per_month === -1 
                      ? t('membership.unlimited')
                      : `${tier.downloads_per_month} ${t('membership.downloadsPerMonth')}`
                    }
                  </p>
                </div>

                <ul className="space-y-4 mb-8">
                  {Array.isArray(tier.features) && tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.exclusive ? 'text-[#E8720C]' : 'text-white/50'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleJoin(tier.id)}
                  className={`w-full py-3.5 rounded-full font-bold transition-all text-sm ${
                    tier.is_ultimate ? 'bg-gradient-to-r from-[#E8720C] to-[#C55A00] hover:from-[#FF8C2E] hover:to-[#E8720C] text-white' :
                    tier.exclusive ? 'btn-gold' : tier.popular ? 'btn-primary' : tier.bestEntry ? 'bg-[#E8720C] hover:bg-[#FF8C2E] text-white' : 'btn-secondary'
                  }`}
                  data-testid={`membership-join-${tier.id}`}
                >
                  {tier.is_ultimate ? `Unirme al Ultimate Pack` : `${t('membership.join')} ${tier.name}`}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OG Elite / Ultimate System Section */}
      <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="glass-card p-8 md:p-12 border-[#E8720C]/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/10 via-transparent to-[#E8720C]/10 blur-3xl" />
            
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-1 text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#E8720C] to-[#C55A00] text-black rounded-full mb-6">
                  <Star className="w-3 h-3" /> Elite Access
                </span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                  OG MUSIC
                  <br />
                  <span className="gold-text">Unlimited System</span>
                </h2>
                <p className="text-lg text-white/60 mb-8">
                  The ultimate music ecosystem access. Unlimited downloads, commercial licensing, 
                  and curated playlist placement opportunities for your releases.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-start gap-3">
                    <Music className="w-5 h-5 text-[#E8720C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Unlimited Downloads</p>
                      <p className="text-xs text-white/50">Full catalog access</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E8720C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Commercial License</p>
                      <p className="text-xs text-white/50">Included with every track</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-[#E8720C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Playlist Ecosystem</p>
                      <p className="text-xs text-white/50">Curated placement visibility</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Star className="w-5 h-5 text-[#E8720C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium text-sm">Priority Support</p>
                      <p className="text-xs text-white/50">1-on-1 assistance</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-baseline gap-3 mb-8">
                  <span className="text-5xl font-bold gold-text">$99</span>
                  <span className="text-white/40">/month</span>
                </div>

                <button 
                  onClick={() => handleJoin('og-unlimited')}
                  className="btn-gold text-lg px-12"
                  data-testid="elite-join-btn"
                >
                  Get Unlimited Access
                </button>
              </div>

              <div className="relative">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/54d51791-75da-4508-8099-24af4d7cd2f0/images/70801011375dc03e65ba02286b51ef6663a7d0b6de4f742712ea5093fda2b635.png"
                  alt="OG Elite Access"
                  className="w-full rounded-2xl animate-float"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <div className="container-custom max-w-3xl">
          <h2 className="text-3xl font-semibold text-center mb-12">
            Frequently Asked <span className="gold-text">Questions</span>
          </h2>

          <div className="space-y-4">
            {[
              {
                q: 'Can I cancel my membership anytime?',
                a: 'Yes, you can cancel your membership at any time. Your access will continue until the end of your billing period.'
              },
              {
                q: 'What is the playlist ecosystem?',
                a: 'OG Unlimited members can submit their released music for consideration in our curated playlist network. This provides visibility and exposure opportunities for your releases.'
              },
              {
                q: 'Do downloads rollover to the next month?',
                a: 'No, unused downloads do not roll over. OG Unlimited members have unlimited downloads so this does not apply to them.'
              },
              {
                q: 'What license is included with membership downloads?',
                a: 'All memberships include commercial licensing with worldwide rights. Basic License is included with every track purchase.'
              },
              {
                q: 'Can I upgrade my plan later?',
                a: 'Yes, you can upgrade your plan at any time. The difference will be prorated for your current billing period.'
              }
            ].map((faq, i) => (
              <div key={i} className="glass-card p-6" data-testid={`faq-item-${i}`}>
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-white/60 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPage;
